import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { useAuth } from "@/contexts/AuthContext";

export type PlanoTratamentoStatus = "Rascunho" | "Enviado" | "Aprovado" | "Em execução" | "Finalizado" | "Recusado";

export interface PlanoTratamentoItem {
  id: string;
  procedimento: string;
  dente?: string;
  quantidade: number;
  valor_unitario: number;
  observacoes?: string;
}

export interface PlanoTratamento {
  id: string;
  paciente_id: string;
  dentista_id?: string;
  titulo: string;
  status: PlanoTratamentoStatus;
  validade?: string;
  desconto?: number;
  itens: PlanoTratamentoItem[];
  observacoes?: string;
  created_at?: string;
  updated_at?: string;
}

export type PlanoTratamentoInput = Omit<PlanoTratamento, "id" | "created_at" | "updated_at">;

export function totalPlanoTratamento(plano: Pick<PlanoTratamento, "itens" | "desconto">) {
  const subtotal = (plano.itens || []).reduce((sum, item) => {
    return sum + Number(item.quantidade || 0) * Number(item.valor_unitario || 0);
  }, 0);
  return Math.max(0, subtotal - Number(plano.desconto || 0));
}

export function usePlanosTratamento() {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [planos, setPlanos] = useState<PlanoTratamento[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPlanos = async () => {
    if (!user) {
      setPlanos([]);
      return;
    }

    try {
      setLoading(true);
      const data = await apiList<PlanoTratamento>("planos_tratamento");
      setPlanos(data.sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || ""))));
    } catch (error: any) {
      toast.error("Erro ao carregar planos de tratamento: " + error.message);
      setPlanos([]);
    } finally {
      setLoading(false);
    }
  };

  const createPlano = async (input: PlanoTratamentoInput) => {
    try {
      const data = await apiCreate<PlanoTratamento>("planos_tratamento", input);
      await fetchPlanos();
      toast.success("Plano de tratamento criado com sucesso!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao criar plano: " + error.message);
      return null;
    }
  };

  const updatePlano = async (id: string, input: Partial<PlanoTratamentoInput>) => {
    try {
      const data = await apiUpdate<PlanoTratamento>("planos_tratamento", id, input);
      await fetchPlanos();
      toast.success("Plano de tratamento atualizado!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao atualizar plano: " + error.message);
      return null;
    }
  };

  const deletePlano = async (id: string) => {
    try {
      await apiDelete("planos_tratamento", id);
      await fetchPlanos();
      toast.success("Plano removido com sucesso!");
    } catch (error: any) {
      toast.error("Erro ao remover plano: " + error.message);
    }
  };

  useEffect(() => {
    fetchPlanos();
  }, [user?.id, activeClinicaId]);

  return { planos, loading, fetchPlanos, createPlano, updatePlano, deletePlano };
}
